import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatMessage } from '../domain/PatientChat';
import { GroupPatientChatApiService } from '../infrastructure/api/GroupPatientChatApiService';

interface UseGroupPatientChatOptions {
  persist?: boolean;
}

/** A conversation with the assistant about one group; `persist` keeps it between visits. */
export const useGroupPatientChat = (groupPatientId: string, options: UseGroupPatientChatOptions = {}) => {
  const { persist = false } = options;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentRequired, setConsentRequired] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setMessages([]);
    setError(null);
    setConsentRequired(false);
    if (persist && groupPatientId) {
      GroupPatientChatApiService.loadHistory(groupPatientId)
        .then((loaded) => setMessages(loaded))
        .catch(() => {/* an empty conversation is a fine start */});
    }
  }, [groupPatientId, persist]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) return;

      const userMessage: ChatMessage = { role: 'user', content: text };
      const currentHistory = messages;
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await GroupPatientChatApiService.sendMessage(groupPatientId, {
          message: text,
          history: currentHistory,
        });
        const updated = [...currentHistory, userMessage, { role: 'model' as const, content: response.data.response }];
        setMessages(updated);
        if (persist) {
          GroupPatientChatApiService.saveHistory(groupPatientId, updated).catch(() => {/* silently ignore */});
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'CanceledError') return;
        const detail = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
        // The server is the one that knows every member's consent; it says so with this code.
        if (detail?.status === 409 && detail.data?.detail === 'ai_consent_required') {
          setConsentRequired(true);
          setMessages(currentHistory);
        } else {
          setError('chat.error');
        }
      } finally {
        setIsLoading(false);
        abortRef.current = null;
      }
    },
    [groupPatientId, messages, persist],
  );

  return { messages, isLoading, error, consentRequired, sendMessage };
};
